// ADR-034 — the summary-first Form 3 cover. When the report-detail payload
// carries the federal Form 3 Detailed Summary Page (a federal, original, Ready Form 3 report),
// the report preview leads with the numbered cover lines the FEC reads first — in TWO columns
// (This Period / Election-cycle-to-date) — plus the cash-on-hand bridge, instead of the flat
// Contributions/Expenditures schedules.
//
// Every summary NUMBER here is DISPLAY ONLY: it comes straight off `FecF3SummaryDto`, which the
// backend computes with the SAME rollup that stamps the `.fec` cover, so the preview cannot drift
// a cent from the file. This component never sums or recomputes a line.
//
// Issue #107 — each summary line is now EXPANDABLE (collapsed by default): expanding a line
// reveals the transactions behind it, and every visible line's rows foot to that line's
// This-Period (Column A) total. The editable lines (11a–d, 12, 14, 15, 17, 18, 19a/b, 21) reuse
// the report-detail's own editable items (regrouped by their resolved FEC line), preserving
// click-through-to-edit + fix-missing-donor-info. The read-only lines (13a/13b loans, 20a–c
// refunds) render the payload's read-only rows; line 17 folds in the synthesized in-kind offset
// so its rows foot. Below the cover: the read-only Schedule C loan ledger and a SINGLE "in this
// period, not on this filing" section (needs-review `Pending` + `Voided` rows, from the
// inclusion-tagged items — one source, never the retired preview "awaiting settlement" block).
import { useState, type ReactNode } from 'react';
import { ChevronRight, AlertTriangle, Pencil } from 'lucide-react';
import {
  PreflightCopy,
  assertNever,
  FEC_F3_COVER_LINES,
  FEC_F3_RECEIPT_LINES,
  FEC_F3_DISBURSEMENT_LINES,
} from '@signalvote/shared';
import type {
  FecF3SummaryLine,
  FecF3SummaryDto,
  FecF3SummaryLineDto,
  FecF3LoanReceiptRowDto,
  FecF3RefundRowDto,
  ContributionReportItem,
  ExpenditureReportItem,
  ReportRowInclusion,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatAmount, formatDate } from '../utils';

// ---------------------------------------------------------------------------
// Props — the summary numbers plus the inclusion-tagged detail items the
// drill-down / off-filing section regroup. Edit callbacks are non-null only on a
// pre-filing (Ready) report; a Filed/Amended snapshot passes null so the drill-down
// rows render read-only (no click-through-to-edit).
// ---------------------------------------------------------------------------
interface Form3SummaryViewProps {
  summary: FecF3SummaryDto;
  contributionItems: ContributionReportItem[];
  expenditureItems: ExpenditureReportItem[];
  requiresEmployer: boolean;
  onEditContribution: ((contributionId: string) => void) | null;
  onEditExpenditure: ((expenditureId: string) => void) | null;
  onFixMissingInfo: (contactId: string) => void;
  /**
   * The report has already filed, so this cover is a replay of the submitted file (the server
   * reads its loans and debts from the filing's own frozen evidence) rather than a preview of an
   * upload still to come. Only the tense of the schedule captions changes — the numbers are built
   * by the same one projection either way.
   */
  asFiled: boolean;
}

// ---------------------------------------------------------------------------
// Row model — one Detailed-Summary-Page line. `cycleToDate: null` renders "—" in
// Column B (the form has no Column B for that line, e.g. the Column-A-only debts).
// `lineKey` marks a line whose transactions can be drilled into (a stable key,
// paren-free, so it works as a test hook / aria id — "11a(i)" → "11ai").
// ---------------------------------------------------------------------------
interface SummaryRow {
  /** Display ref, e.g. "6a", "11a(i)", "10". */
  ref: string;
  /** FEC Form 3 boilerplate label. */
  label: string;
  thisPeriod: number;
  cycleToDate: number | null;
  /** Total / net lines render bold. */
  bold?: boolean;
  /** Sub-lines that roll up into a total render indented. */
  indent?: boolean;
  /** Optional stable test hook (used by line 10). */
  testId?: string;
  /** Drill-down key — present on lines whose detail rows can be expanded. */
  lineKey?: string;
}

/**
 * ADR-051 — the Form 3 line grid (numbers, boilerplate labels, which lines carry a Column B, and
 * which have expandable detail) is SHARED with the mobile report panel: a form's grid is a fact
 * about a paper document, and transcribing it a second time is how half the mobile panel's labels
 * first drifted from the ones shown here for the same lines. Line 8 (cash on hand at close) is
 * absent from the grid on purpose — it is the same figure as the cash-bridge ending (line 27), and
 * the page shows exactly one ending-cash figure, the bridge's.
 */
function toRow(s: FecF3SummaryDto, line: FecF3SummaryLine): SummaryRow {
  const shared = {
    ref: line.ref,
    label: line.label,
    bold: line.bold,
    indent: line.indent,
    lineKey: line.lineKey,
    // The one view-only hook, kept here rather than in the shared grid.
    testId: line.ref === '10' ? 'form3-line-10' : undefined,
  };

  return line.columns === 'two'
    ? { ...shared, thisPeriod: s[line.field].thisPeriod, cycleToDate: s[line.field].cycleToDate }
    : { ...shared, thisPeriod: s[line.field], cycleToDate: null };
}

const coverRows = (s: FecF3SummaryDto): SummaryRow[] =>
  FEC_F3_COVER_LINES.map((line) => toRow(s, line));

const receiptRows = (s: FecF3SummaryDto): SummaryRow[] =>
  FEC_F3_RECEIPT_LINES.map((line) => toRow(s, line));

const disbursementRows = (s: FecF3SummaryDto): SummaryRow[] =>
  FEC_F3_DISBURSEMENT_LINES.map((line) => toRow(s, line));

// ---------------------------------------------------------------------------
// Drill-down rows — compact, one line each, reading like a filing. Contribution /
// expenditure rows carry the two required editable affordances (click-through-to-edit
// + fix-missing-donor-info) when the report is pre-filing; payload rows (loans /
// refunds / the in-kind offset) are always read-only.
// ---------------------------------------------------------------------------
const DRILL_ROW = 'flex items-baseline gap-2 border-b border-slate-100 py-1.5 last:border-0 text-sm';

function DrillContributionRow({
  item,
  requiresEmployer,
  onEdit,
  onFixMissingInfo,
}: {
  item: ContributionReportItem;
  requiresEmployer: boolean;
  onEdit: ((id: string) => void) | null;
  onFixMissingInfo: (contactId: string) => void;
}) {
  // The per-row missing-employer/occupation badge. `missingEmployerInfo` is server-produced
  // through the shared donor-info verdict (the itemized, on-filing twin of the report-level
  // missing-donor-info banner), so this is a plain typed read.
  const hasMissingInfo = requiresEmployer && item.missingEmployerInfo;
  return (
    <div className={DRILL_ROW} data-testid={`drill-contribution-${item.id}`}>
      <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{formatDate(item.date)}</span>
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        <span className="text-foreground">{item.contactName}</span>
        {onEdit && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onEdit(item.id)}
            aria-label={`Edit this contribution: ${item.contactName}`}
            title="Open in the ledger to fix or confirm this contribution"
            className="h-8 w-8 shrink-0 text-primary"
          >
            <Pencil aria-hidden="true" />
          </Button>
        )}
        {hasMissingInfo && (
          <button
            type="button"
            data-testid={`drill-missing-info-${item.id}`}
            aria-label={`Fix missing employer/occupation for ${item.contactName}`}
            title="Missing employer/occupation — click to fix"
            onClick={() => onFixMissingInfo(item.contactId)}
            className="rounded p-0.5 hover:bg-amber-100"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
          </button>
        )}
      </span>
      <span className="w-24 shrink-0 text-right font-medium tabular-nums text-foreground">
        {formatAmount(item.amount)}
      </span>
    </div>
  );
}

function DrillExpenditureRow({
  item,
  onEdit,
}: {
  item: ExpenditureReportItem;
  onEdit: ((id: string) => void) | null;
}) {
  return (
    <div className={DRILL_ROW} data-testid={`drill-expenditure-${item.id}`}>
      <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{formatDate(item.date)}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-1">
          <span className="text-foreground">{item.contactName}</span>
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onEdit(item.id)}
              aria-label={`Edit this expenditure: ${item.contactName}`}
              title="Open in the ledger to fix or confirm this expenditure"
              className="h-8 w-8 shrink-0 text-primary"
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
        </span>
        {item.purpose && <span className="text-xs text-muted-foreground">{item.purpose}</span>}
      </span>
      <span className="w-24 shrink-0 text-right font-medium tabular-nums text-foreground">
        {formatAmount(item.amount)}
      </span>
    </div>
  );
}

/** Read-only payload row (loan proceeds received, or a contribution refund paid). */
function DrillPayloadRow({
  testId,
  date,
  name,
  detail,
  amount,
}: {
  testId: string;
  date: string;
  name: string;
  detail?: string;
  amount: number;
}) {
  return (
    <div className={DRILL_ROW} data-testid={testId}>
      <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{formatDate(date)}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground">{name}</span>
        {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
      </span>
      <span className="w-24 shrink-0 text-right font-medium tabular-nums text-foreground">
        {formatAmount(amount)}
      </span>
    </div>
  );
}

/** The synthesized 11 CFR 104.13(a)(2) in-kind offset folded into line 17 so its rows foot. */
function DrillInKindOffsetRow({ amount }: { amount: number }) {
  return (
    <div className={DRILL_ROW} data-testid="drill-inkind-offset">
      <span className="w-20 shrink-0 text-muted-foreground">—</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="italic text-foreground">In-kind offset</span>
        <span className="text-xs text-muted-foreground">
          Fair-market value of in-kind contributions (11 CFR 104.13(a)(2)) — auto-added so line 17 foots.
        </span>
      </span>
      <span className="w-24 shrink-0 text-right font-medium tabular-nums text-foreground">
        {formatAmount(amount)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------
// One shared 4-column track — ref · label · This-period · Cycle-to-date — so the
// header labels and every body figure land in the SAME columns (a ledger reads
// wrong the instant its amount columns drift). Fixed amount columns keep the two
// money columns tight to the labels instead of flung to a far edge; the whole
// cover is width-capped by the parent so a wide monitor can't stretch the rows.
const GRID_COLS =
  'grid grid-cols-[3.75rem_minmax(0,1fr)_7rem_7rem] items-center gap-x-3';

function SummaryTable({
  title,
  rows,
  drills,
  openLines,
  onToggle,
}: {
  title: string;
  rows: SummaryRow[];
  /** lineKey → drill content; a row is expandable iff its lineKey is present here. */
  drills: Record<string, ReactNode>;
  openLines: Set<string>;
  onToggle: (lineKey: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <div className={`${GRID_COLS} bg-slate-50 px-3 py-2`}>
        <h4 className="col-span-2 text-sm font-semibold text-foreground">{title}</h4>
        <span className="text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          This period
        </span>
        <span className="text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Cycle-to-date
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => {
          const drill = r.lineKey ? drills[r.lineKey] : undefined;
          const expandable = !!drill;
          const isOpen = r.lineKey ? openLines.has(r.lineKey) : false;
          const amounts = (
            <>
              <span
                className={`text-right text-sm tabular-nums ${
                  r.bold ? 'font-bold' : 'font-medium'
                } text-foreground`}
              >
                {formatAmount(r.thisPeriod)}
              </span>
              <span className="text-right text-sm tabular-nums text-muted-foreground">
                {r.cycleToDate === null ? '—' : formatAmount(r.cycleToDate)}
              </span>
            </>
          );
          const refCell = (
            <span className="flex items-center gap-1 font-mono text-xs tabular-nums text-muted-foreground">
              {expandable ? (
                <ChevronRight
                  className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  aria-hidden="true"
                />
              ) : (
                <span className="w-3.5 shrink-0" aria-hidden="true" />
              )}
              {r.ref}
            </span>
          );
          const label = (
            <span
              className={`min-w-0 text-sm ${r.indent ? 'pl-2' : ''} ${
                r.bold ? 'font-bold text-foreground' : 'text-foreground'
              }`}
            >
              {r.label}
            </span>
          );

          if (!expandable) {
            return (
              <div
                key={r.ref}
                data-testid={r.testId}
                className={`${GRID_COLS} px-3 py-1.5 ${r.bold ? 'bg-slate-50/70' : ''}`}
              >
                {refCell}
                {label}
                {amounts}
              </div>
            );
          }

          const panelId = `drill-panel-${r.lineKey}`;
          // WAI-ARIA disclosure pattern: a native <button> toggle with
          // aria-expanded + aria-controls pointing at the panel. Focus intentionally
          // STAYS on the trigger on expand/collapse (a disclosure, not a dialog), so
          // keyboard users toggle in place without a focus jump.
          return (
            <div key={r.ref} data-testid={r.testId}>
              <button
                type="button"
                data-testid={`drill-toggle-${r.lineKey}`}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => onToggle(r.lineKey!)}
                className={`${GRID_COLS} w-full px-3 py-1.5 text-left hover:bg-slate-50`}
              >
                {refCell}
                {label}
                {amounts}
              </button>
              {isOpen && (
                <div
                  id={panelId}
                  data-testid={panelId}
                  role="region"
                  aria-label={`${r.ref} — ${r.label} detail`}
                  className="border-l-2 border-sky-200 bg-slate-50/40 py-1 pl-11 pr-3"
                >
                  {drill}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CashBridge({ bridge }: { bridge: FecF3SummaryDto['cashOnHand'] }) {
  // The five cash-continuity steps (Form 3 lines 23–27). The final "Cash at close"
  // cell is the single ending-cash figure on the whole page (== line 8 == the
  // payload's displayed ending cash).
  const steps: { label: string; value: number; tone: string; testId?: string }[] = [
    { label: 'Beginning cash', value: bridge.beginning, tone: 'text-slate-700' },
    { label: '+ Receipts', value: bridge.totalReceipts, tone: 'text-emerald-700' },
    { label: 'Subtotal', value: bridge.subtotal, tone: 'text-slate-700' },
    { label: '− Disbursements', value: bridge.totalDisbursements, tone: 'text-rose-700' },
    {
      label: 'Cash at close',
      value: bridge.ending,
      tone: 'font-bold text-slate-900',
      testId: 'form3-cash-bridge-ending',
    },
  ];
  return (
    <div
      data-testid="form3-cash-bridge"
      className="grid grid-cols-2 divide-slate-200 rounded-lg border border-slate-200 bg-slate-50/60 sm:grid-cols-5 sm:divide-x"
    >
      {steps.map((s) => (
        <div key={s.label} className="px-3 py-3 text-center" data-testid={s.testId}>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{s.label}</div>
          <div className={`mt-1 text-sm tabular-nums ${s.tone}`}>{formatAmount(s.value)}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only Schedule C loan ledger — the loans outstanding at close behind line 10
// (as it will be filed). No loan/debt CRUD lives here.
// ---------------------------------------------------------------------------
// The one caption tense both balance schedules read — a filed report's Schedule C and D are a
// replay of the document, not a preview of one still to be uploaded.
const basisCaption = (asFiled: boolean) => (asFiled ? 'As filed.' : 'As it will be filed.');

function ScheduleCLoansSection({ summary, asFiled }: { summary: FecF3SummaryDto; asFiled: boolean }) {
  if (summary.scheduleCLoans.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-violet-200" data-testid="schedule-c-loans">
      <div className="bg-violet-50 px-3 py-2">
        <h4 className="text-sm font-semibold text-violet-900">
          Schedule C — Loans outstanding at close
        </h4>
        <p className="text-xs text-violet-900/70">
          {basisCaption(asFiled)} Manage loans in the ledger.
        </p>
      </div>
      <div className="divide-y divide-violet-100 px-3">
        {summary.scheduleCLoans.map((loan) => (
          <div key={loan.loanId} className="py-2 text-sm" data-testid={`schedule-c-loan-${loan.loanId}`}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-foreground">{loan.lenderName}</span>
              <span className="tabular-nums text-foreground">{formatAmount(loan.balanceAtClose)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Line {loan.lineRef} · {loan.isPersonalFunds ? 'candidate personal funds' : 'other lender'} ·
              incurred {formatDate(loan.incurredDate)} · original {formatAmount(loan.originalAmount)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only Schedule D debt ledger (ADR-031 / #92) — the non-loan obligations behind the OTHER
// half of line 10, each with its four date-windowed period columns (begin / incurred / payment /
// end), as they will be filed. Mirrors the Schedule C loan section; manage debts in Campaign
// Settings. Renders nothing when the report has no debts.
// ---------------------------------------------------------------------------
function ScheduleDDebtsSection({ summary, asFiled }: { summary: FecF3SummaryDto; asFiled: boolean }) {
  if (summary.scheduleDDebts.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-violet-200" data-testid="schedule-d-debts">
      <div className="bg-violet-50 px-3 py-2">
        <h4 className="text-sm font-semibold text-violet-900">
          Schedule D — Debts &amp; obligations owed by the committee
        </h4>
        <p className="text-xs text-violet-900/70">
          {basisCaption(asFiled)} Manage debts in Campaign Settings.
        </p>
      </div>
      <div className="divide-y divide-violet-100 px-3">
        {summary.scheduleDDebts.map((debt) => (
          <div key={debt.debtId} className="py-2 text-sm" data-testid={`schedule-d-debt-${debt.debtId}`}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-foreground">
                {debt.creditorName} — {debt.purpose}
              </span>
              <span className="tabular-nums text-foreground">{formatAmount(debt.end)} owed at close</span>
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">
              Begin {formatAmount(debt.begin)} · incurred {formatAmount(debt.incurred)} ·
              paid {formatAmount(debt.payment)} · end {formatAmount(debt.end)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The single "in this period, not on this filing" section — the inclusion-tagged
// detail rows that are off the filing (one source). A `Pending` row is off because
// it is not yet approved (Confirmed) — "needs review", NOT "awaiting settlement"
// (settlement is not a filing gate). Replaces the retired preview-driven block.
// ---------------------------------------------------------------------------
type OffFilingKind = 'Pending' | 'Voided' | 'Drift';

interface OffFilingRow {
  id: string;
  date: string;
  name: string;
  amount: number;
  kind: OffFilingKind;
}

// Exhaustive over the whole inclusion union (Included + Reported map to null —
// both are ON the filing/report, not off it). `assertNever` makes adding a new inclusion a
// compile error rather than a row silently dropped from the off-filing section.
function offFilingKind(inclusion: ReportRowInclusion): OffFilingKind | null {
  switch (inclusion) {
    case 'Included':
    case 'Reported':
      return null;
    case 'ExcludedPending':
      return 'Pending';
    case 'ExcludedVoided':
      return 'Voided';
    case 'ExcludedStatusDrift':
      return 'Drift';
    default:
      return assertNever(inclusion);
  }
}

// Treasurer-facing labels come from PreflightCopy (the render-boundary copy source,
// like the flat schedule's `excludedInclusionLabel`); the tooltips are contextual
// help unique to this section. Pending reads "needs review" — off the filing because
// it isn't approved yet, NOT because funds haven't settled (settlement is not a gate).
const OFF_FILING_BADGE: Record<OffFilingKind, { label: string; className: string; title: string }> = {
  Pending: {
    label: PreflightCopy.ScheduleOffFilingNeedsReviewLabel,
    className: 'bg-amber-100 text-amber-800',
    title: "Off the filing because it hasn't been approved yet. Approve it when you file to include it.",
  },
  Voided: {
    label: PreflightCopy.ScheduleExcludedVoidedLabel,
    className: 'bg-muted text-muted-foreground',
    title: 'Voided — intentionally left off the filing.',
  },
  Drift: {
    label: PreflightCopy.ScheduleExcludedStatusDriftLabel,
    className: 'bg-muted text-muted-foreground',
    title: 'The transaction changed status before filing, so it is off this filing.',
  },
};

function OffFilingSection({ rows }: { rows: OffFilingRow[] }) {
  if (rows.length === 0) return null;
  const hasPending = rows.some((r) => r.kind === 'Pending');
  return (
    <div
      id="off-filing-section"
      data-testid="off-filing-section"
      className="scroll-mt-4 overflow-hidden rounded-lg border border-muted bg-muted/30"
    >
      <div className="px-3 py-2">
        <h4 className="text-sm font-semibold text-foreground">In this period, not on this filing</h4>
        <p className="text-xs text-muted-foreground">
          These are in the period but off the filing — they don&apos;t count toward the totals above.
        </p>
      </div>
      <div className="divide-y divide-slate-100 px-3">
        {rows.map((row) => {
          const badge = OFF_FILING_BADGE[row.kind];
          return (
            <div
              key={row.id}
              data-testid={`off-filing-${row.id}`}
              className="flex items-baseline gap-2 py-1.5 text-sm text-muted-foreground"
            >
              <span className="w-20 shrink-0 tabular-nums">{formatDate(row.date)}</span>
              <span className="min-w-0 flex-1 truncate">{row.name}</span>
              <span className="w-24 shrink-0 text-right tabular-nums">{formatAmount(row.amount)}</span>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                title={badge.title}
              >
                {badge.label}
              </span>
            </div>
          );
        })}
      </div>
      {hasPending && (
        <p className="border-t border-slate-100 px-3 py-2 text-xs text-muted-foreground">
          Approving a needs-review row when you file adds it to the totals above — that&apos;s the
          expected sequence, not a discrepancy.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The summary-first cover
// ---------------------------------------------------------------------------
export function Form3SummaryView({
  summary,
  contributionItems,
  expenditureItems,
  requiresEmployer,
  onEditContribution,
  onEditExpenditure,
  onFixMissingInfo,
  asFiled,
}: Form3SummaryViewProps) {
  // Independent per-line disclosure — any number of lines can be open at once,
  // all collapsed by default.
  const [openLines, setOpenLines] = useState<Set<string>>(() => new Set());
  const toggle = (lineKey: string) =>
    setOpenLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineKey)) next.delete(lineKey);
      else next.add(lineKey);
      return next;
    });

  // Only the filed (Included) rows appear in a line's drill-down, so the visible
  // rows foot to that line's This-Period (Column A) total. The off-filing rows are
  // the inclusion-tagged remainder, surfaced in their own section below.
  const includedContribs = contributionItems.filter((i) => i.inclusion === 'Included');
  const includedExps = expenditureItems.filter((i) => i.inclusion === 'Included');
  const contribsOnLine = (line: string) => includedContribs.filter((i) => i.fecLine === line);
  const expsOnLine = (line: string) => includedExps.filter((i) => i.fecLine === line);

  // Individual contributions (line 11a) split on the server-computed itemization
  // verdict — 11a(i) itemized, 11a(ii) unitemized — so each foots to its own total.
  const indiv = contribsOnLine('11a');
  const indivItemized = indiv.filter((i) => i.isItemized);
  const indivUnitemized = indiv.filter((i) => !i.isItemized);

  const contribRows = (items: ContributionReportItem[]): ReactNode =>
    items.map((item) => (
      <DrillContributionRow
        key={item.id}
        item={item}
        requiresEmployer={requiresEmployer}
        onEdit={onEditContribution}
        onFixMissingInfo={onFixMissingInfo}
      />
    ));
  const expRows = (items: ExpenditureReportItem[]): ReactNode =>
    items.map((item) => <DrillExpenditureRow key={item.id} item={item} onEdit={onEditExpenditure} />);
  const loanRows = (rows: FecF3LoanReceiptRowDto[]): ReactNode =>
    rows.map((loan) => (
      <DrillPayloadRow
        key={loan.loanId}
        testId={`drill-loan-${loan.loanId}`}
        date={loan.date}
        name={loan.lenderName}
        detail={loan.isPersonalFunds ? 'Candidate personal funds' : 'Other lender'}
        amount={loan.amount}
      />
    ));
  const refundRows = (rows: FecF3RefundRowDto[]): ReactNode =>
    rows.map((refund) => (
      <DrillPayloadRow
        key={refund.refundId}
        testId={`drill-refund-${refund.refundId}`}
        date={refund.date}
        name={refund.recipientName}
        amount={refund.amount}
      />
    ));

  // Build the drill content for every drillable line; a line is expandable only
  // when it has at least one row. Editable lines reuse the report-detail items
  // (regrouped by resolved FEC line); read-only lines render the payload's rows.
  const drills: Record<string, ReactNode> = {};
  const addDrill = (key: string, items: unknown[], node: ReactNode) => {
    if (items.length > 0) drills[key] = node;
  };
  addDrill('11ai', indivItemized, contribRows(indivItemized));
  if (indivUnitemized.length > 0) {
    drills['11aii'] = (
      <div className="space-y-1">
        <p className="text-xs italic text-muted-foreground">
          {indivUnitemized.length} unitemized{' '}
          {indivUnitemized.length === 1 ? 'donor' : 'donors'} under the itemization threshold — filed
          as one combined total, listed here so you can still inspect each.
        </p>
        {contribRows(indivUnitemized)}
      </div>
    );
  }
  for (const key of ['11b', '11c', '11d', '12', '14', '15'] as const) {
    addDrill(key, contribsOnLine(key), contribRows(contribsOnLine(key)));
  }
  for (const key of ['18', '19a', '19b', '21'] as const) {
    addDrill(key, expsOnLine(key), expRows(expsOnLine(key)));
  }
  // Line 17 folds the synthesized in-kind offset in with the operating rows so the
  // drill-down foots to line 17's total (the paired in-kind receipt stays on 11a).
  const exp17 = expsOnLine('17');
  const inKindOffset = summary.inKindOffset && summary.inKindOffset.amount !== 0 ? summary.inKindOffset : null;
  if (exp17.length > 0 || inKindOffset) {
    drills['17'] = (
      <>
        {expRows(exp17)}
        {inKindOffset && <DrillInKindOffsetRow amount={inKindOffset.amount} />}
      </>
    );
  }
  for (const key of ['13a', '13b'] as const) {
    const loans = summary.loanReceipts.filter((l) => l.line === key);
    addDrill(key, loans, loanRows(loans));
  }
  for (const key of ['20a', '20b', '20c'] as const) {
    const refunds = summary.refunds.filter((r) => r.line === key);
    addDrill(key, refunds, refundRows(refunds));
  }

  // Reconciliation note (folds in the old Schedule A dual-count): in-period receipt
  // count vs. what's on the filing, with a jump to the off-filing section. In-period
  // = filed rows + rows still needing review (Pending); the will-be-filed figure is
  // the Included set.
  const pendingContribs = contributionItems.filter((i) => i.inclusion === 'ExcludedPending');
  const onFilingReceipts = includedContribs.length;
  const needReviewReceipts = pendingContribs.length;
  const inPeriodReceipts = onFilingReceipts + needReviewReceipts;

  // The single off-filing section: every inclusion-tagged detail row that is off the
  // filing (contributions then expenditures), one source.
  const offFilingRows: OffFilingRow[] = [
    ...contributionItems,
    ...expenditureItems,
  ]
    .map((i) => {
      const kind = offFilingKind(i.inclusion);
      return kind ? { id: i.id, date: i.date, name: i.contactName, amount: i.amount, kind } : null;
    })
    .filter((r): r is OffFilingRow => r !== null);

  const scrollToOffFiling = () =>
    document.getElementById('off-filing-section')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  return (
    // The parent report page caps + centers the whole summary-first view to a document
    // measure, so this fills that shared width — the amount columns stay tight to their
    // labels without a full-bleed ledger stretching across a wide monitor.
    <div className="space-y-5" data-testid="form3-summary-view">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Detailed Summary Page</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          This is what the FEC reads first: the numbered cover lines your filing rolls up to,
          in two columns (this period and election cycle-to-date). Expand any line to see the
          transactions behind it.
          {asFiled && ' These are the figures on the file you submitted, not today\u2019s balances.'}
        </p>
      </div>

      <SummaryTable title="Financial summary" rows={coverRows(summary)} drills={drills} openLines={openLines} onToggle={toggle} />

      <div className="space-y-2">
        <SummaryTable title="Receipts" rows={receiptRows(summary)} drills={drills} openLines={openLines} onToggle={toggle} />
        {/* Reconciliation note — in-period vs on-filing receipt counts, linking to
            the off-filing section (folds in the old Schedule A dual-count). */}
        <p className="px-1 text-xs text-muted-foreground" data-testid="receipts-reconciliation-note">
          In this period: {inPeriodReceipts} {inPeriodReceipts === 1 ? 'receipt' : 'receipts'} · On this
          filing: {onFilingReceipts}
          {needReviewReceipts > 0 && (
            <>
              {' — '}
              <button
                type="button"
                data-testid="reconciliation-note-link"
                onClick={scrollToOffFiling}
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
              >
                {needReviewReceipts} {needReviewReceipts === 1 ? 'needs' : 'need'} review
              </button>
            </>
          )}
        </p>
      </div>

      <SummaryTable title="Disbursements" rows={disbursementRows(summary)} drills={drills} openLines={openLines} onToggle={toggle} />

      <div>
        <h4 className="mb-2 text-sm font-semibold text-foreground">Cash on hand</h4>
        <CashBridge bridge={summary.cashOnHand} />
      </div>

      <ScheduleCLoansSection summary={summary} asFiled={asFiled} />
      <ScheduleDDebtsSection summary={summary} asFiled={asFiled} />

      <OffFilingSection rows={offFilingRows} />
    </div>
  );
}
